/* eslint-disable react/jsx-handler-names */
import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import queryString from 'querystring';
import loadable from '@loadable/component';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { TYPE_ERROR } from '@eb/eds-notification';
import { AlertChunky } from '@eb/eds-iconography';
import { withMainControls } from '@eb/eds-structure';
import { trackAnalyticsEvent } from '@eb/site-analytics';
import { getAction, cleanAction } from '@eb/user-action-cookie';

import { trackPageView as trackPageViewAction } from '../../../../redux/actions/analytics';
import { setSearchInformationFromUrl as setSearchInformationFromUrlAction } from '../../../../redux/actions/location';
import { updateSearchInformationFromUrl as updateSearchInformationFromUrlAction } from '../../../../redux/actions/search';

import { SearchResultsPanelContainer } from '../SearchResultsPanelContainer';
import { LazyMapPanelContainer } from '../MapPanelContainer';
import { SearchPivotsContainer } from '../SearchPivotsContainer';
import { SearchFooterContainer } from '../SearchFooterContainer';

const FilterPanelContainer = loadable(() => import('../FilterPanelContainer'), {
    resolveComponent: (component) => component.FilterPanelContainer,
});

import {
    GA_SEARCH_CATEGORY,
    GA_FOLLOWED_ORGANIZERS_FILTER,
} from '../../../../constants/analytics';

import './SearchBaseContainer.scss';

export class SearchBaseScreen extends React.PureComponent {
    static propTypes = {
        /**
         * GA Utility for tracking the pageview of search
         */
        onTrackPageViewAction: PropTypes.func.isRequired,
        /**
         * Sets current search information state from the URL
         */
        setSearchInformationFromUrl: PropTypes.func.isRequired,
        /**
         * Gets current search information state from the URL
         */
        updateSearchInformationFromUrl: PropTypes.func.isRequired,
        /**
         * History object from React Router
         */
        // eslint-disable-next-line
        history: PropTypes.object,
        /**
         * Type of individual filter to show in the filter panel
         * or '' if all filters must be shown
         */
        filterTypeToShow: PropTypes.string,
    };

    componentDidMount() {
        const {
            isAuthenticated,
            onTrackPageViewAction,
            setSearchInformationFromUrl,
            eventCount,
        } = this.props;

        // set the pair location-searchCriterias so we can use them later when
        // the user try to navigate back
        setSearchInformationFromUrl(
            this.props.history.location.pathname,
            queryString.parse(this.props.history.location.search),
        );

        // track pageview GA action
        onTrackPageViewAction();

        if (isAuthenticated) {
            const action = getAction();

            if (action && action.action === 'FollowedOrganizerFilterAttempt') {
                cleanAction();

                trackAnalyticsEvent({
                    category: GA_SEARCH_CATEGORY,
                    action: GA_FOLLOWED_ORGANIZERS_FILTER,
                    label: eventCount,
                });
            }
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.location.pathname + prevProps.location.search !==
            this.props.location.pathname + this.props.location.search
        ) {
            // TODO: - MD: Appears to be the source of bug for EB-131855
            // TRIED: encodeURIComponent on pathname, but it is already PARTIALLY encoded % was not encoded, but $ was encoded to %26
            // This is for supporting the back button. We can remove
            // this if we refactor the app to derive the page state
            // directly from the URL
            this.props.updateSearchInformationFromUrl(
                this.props.location.pathname,
                queryString.parse(this.props.location.search.replace('?', '')),
            );
        }

        //Handle rendering errors related to search or page changes
        if (
            !!this.props.showErrorNotification &&
            !prevProps.showErrorNotification
        ) {
            this.props.addMainNotification({
                type: TYPE_ERROR,
                children: this.props.showErrorNotification,
                iconType: <AlertChunky />,
                hasCloseButton: true,
            });
        }

        if (
            !this.props.showErrorNotification &&
            !!prevProps.showErrorNotification
        ) {
            this.props.hideMainNotification();
        }
    }

    render() {
        const { isAnimatedDrawerOpen, isOnline, isBotRequest } = this.props;

        //TODO: Only import FilterPanelContainer if mobile screen
        return (
            <div
                className={classnames('search-base-screen__root', {
                    'search-base-screen--is-mobile-filter-open': isAnimatedDrawerOpen,
                    'search-base-screen--bot-request': isBotRequest,
                })}
            >
                <FilterPanelContainer />
                <section className="search-base-screen__search-panel">
                    <SearchResultsPanelContainer history={this.props.history} />
                    <SearchFooterContainer />
                    <SearchPivotsContainer />
                </section>

                <section className="search-base-screen__map-panel">
                    <LazyMapPanelContainer isOnline={isOnline} />
                </section>
            </div>
        );
    }
}

const _mapStateToProps = (state) => ({
    isAnimatedDrawerOpen: state.ui.isAnimatedDrawerOpen,
    showErrorNotification: state.ui.showErrorNotification,
    eventCount:
        state.pagination.event[Object.keys(state.pagination.event)[0]]
            ?.objectCount,
    //Props below for withEventSaveNotification HoC
    isAuthenticated: state.auth.isAuthenticated,
    isOnline: state.location.isOnline,
    isBotRequest: state.app.isBotRequest,
});

const _mapDispatchToProps = (dispatch, ownProps) => ({
    setSearchInformationFromUrl: (...args) =>
        dispatch(setSearchInformationFromUrlAction(...args)),
    updateSearchInformationFromUrl: (...args) =>
        dispatch(updateSearchInformationFromUrlAction(...args)),
    onTrackPageViewAction: (...args) => dispatch(trackPageViewAction(...args)),
});

const enhance = compose(
    withMainControls,
    withRouter,
    connect(_mapStateToProps, _mapDispatchToProps),
);

export const EnhancedSearchBaseScreen = enhance(SearchBaseScreen);
